import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { AssetCRUD } from "./api"

/**
 * Load Asset dialog — pick an existing asset (any ad) to copy into the
 * current ad. Mirrors the CMS type-selector dialog: search box + scrollable
 * autocomplete list. onSelect receives the chosen asset doc (html + name);
 * the caller seeds a new asset from it.
 */
export default function AssetSelectorDialog({ isOpen, onSelect, onClose }) {
  const [query, setQuery] = useState("")
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    ;(async () => {
      const res = await AssetCRUD.Get({
        match: { deleted: { $ne: true } },
        page: 1,
        itemsPerPage: 500,
        sort: { created: -1 },
      }).catch(() => null)
      setAssets(res?.data || [])
      setLoading(false)
    })()
    if (searchRef.current) searchRef.current.focus()
  }, [isOpen])

  const filtered = assets.filter((a) =>
    (a?.name || "").toLowerCase().includes(query.toLowerCase())
  )

  const pick = (asset) => {
    onSelect(asset)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_compact"
      closeOnEscape
      className="w-[600px] h-[80vh] p-6"
    >
      <div className="flex flex-col text-admin_text h-full">
        <div className="flex-shrink-0 px-3">
          <Typography component="h2" variant="admin_h4" className="text-center mb-4">
            Load an existing asset
          </Typography>
          <Input
            variant="admin"
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assets by name"
          />
        </div>

        <div className="h-[55vh] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_1 scrollbar-track-admin_paper mt-4">
          {loading ? (
            <Typography variant="admin_body1_dim" className="text-center">Loading…</Typography>
          ) : filtered.length ? (
            filtered.map((asset) => (
              <div
                key={asset._id}
                className="flex justify-between items-center border-b border-admin_bordered hover:bg-admin_canvas cursor-pointer p-3"
                onClick={() => pick(asset)}
              >
                <div className="flex flex-row items-center min-w-0">
                  <div className="w-12 h-12 flex items-center justify-center bg-admin_neutral_2 rounded-md mr-4 shrink-0">
                    {(asset.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col min-w-0">
                    <Typography variant="admin_label" className="mb-0 truncate">{asset.name}</Typography>
                    <Typography variant="admin_txt1_dim" className="truncate">
                      {asset.created ? new Date(asset.created).toLocaleDateString() : ""}
                      {asset.enabled === false ? " · disabled" : ""}
                    </Typography>
                  </div>
                </div>
                <Button><ChevronRightIcon className="w-6 h-6" /></Button>
              </div>
            ))
          ) : (
            <Typography variant="admin_body1_dim" className="text-center">No assets found.</Typography>
          )}
        </div>
      </div>
    </Modal>
  )
}
